import React, { useEffect, useRef, useState } from "react";
import {
  FaCalendar,
  FaEdit,
  FaExchangeAlt,
  FaHome,
  FaPlus,
  FaRupeeSign,
  FaTimes,
  FaTrash,
  FaUser,
} from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import ConfirmDialog from "../components/ConfirmDialog";
import "../css/components/BookingModals.css";
import "../css/pages/AddBooking.css";
import { useHiddenFields } from "../hooks/useHiddenFields";
import { usePageTitle } from "../hooks/usePageTitle";
import bookingService, {
  Booking,
  BookingApplicant,
  BookingPaymentPlan,
  Unit,
  UpdateBookingData,
} from "../services/bookingService";
import { Broker, brokerService } from "../services/brokerService";
import { Project, projectService } from "../services/projectService";
import { filterUnitsBySearch, formatUnitDisplay } from "../utils/unitSearch";

const EditBooking: React.FC = () => {
  usePageTitle();
  const { hiddenFields } = useHiddenFields();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("id");

  // Get current user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user?.isSuperAdmin || false;

  const [booking, setBooking] = useState<Booking | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [availableUnits, setAvailableUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Form states
  const [formData, setFormData] = useState<UpdateBookingData>({
    saleDeedAmount: 0,
    chargesAmount: 0,
    otherWorkAmount: 0,
    terraceAmount: 0,
    banakhatAmount: 0,
    applicants: [],
    paymentTerms: "installments",
  });
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Unit search states
  const [unitSearchTerm, setUnitSearchTerm] = useState("");
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);

  // Transfer unit search states
  const [transferUnitSearchTerm, setTransferUnitSearchTerm] = useState("");
  const [showTransferUnitDropdown, setShowTransferUnitDropdown] =
    useState(false);

  // Payment plan states
  const [paymentPlans, setPaymentPlans] = useState<BookingPaymentPlan[]>([]);

  // Dropdown ref for click outside functionality
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Confirmation dialogs for dangerous actions
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    type: "cancel" | "delete" | "transfer" | "";
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    type: "",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  // Password confirmation for dangerous actions
  const [passwordDialog, setPasswordDialog] = useState<{
    isOpen: boolean;
    password: string;
    error: string;
  }>({
    isOpen: false,
    password: "",
    error: "",
  });

  // Edit reason dialog for authorized bookings
  const [editReasonDialog, setEditReasonDialog] = useState<{
    isOpen: boolean;
    editReason: string;
    editDetails: string;
    error: string;
  }>({
    isOpen: false,
    editReason: "",
    editDetails: "",
    error: "",
  });

  // Cancel booking modal
  const [cancelModal, setCancelModal] = useState<{
    isOpen: boolean;
    reason: string;
    error: string;
  }>({
    isOpen: false,
    reason: "",
    error: "",
  });

  // Transfer booking modal
  const [transferModal, setTransferModal] = useState<{
    isOpen: boolean;
    transferType: "name" | "unit";
    newUnitId: number;
    newApplicants: Omit<BookingApplicant, "id">[];
    newAmounts: {
      saleDeedAmount: number;
      chargesAmount: number;
      otherWorkAmount: number;
      terraceAmount: number;
      banakhatAmount: number;
    };
    reason: string;
    error: string;
  }>({
    isOpen: false,
    transferType: "name",
    newUnitId: 0,
    newApplicants: [
      {
        firstName: "",
        fatherHusbandName: "",
        surname: "",
        contactNumber: "",
        isPrimary: true,
      },
    ],
    newAmounts: {
      saleDeedAmount: 0,
      chargesAmount: 0,
      otherWorkAmount: 0,
      terraceAmount: 0,
      banakhatAmount: 0,
    },
    reason: "",
    error: "",
  });

  // Source options
  const sourceOptions = [
    "Family/Friends",
    "Walk-in",
    "Hoarding Ads",
    "Newspaper Ads",
    "Pamphlet Ads",
    "Facebook",
    "Instagram",
    "Website",
    "Other",
  ];

  // Phone numbers state - array of {countryCode, number} per applicant
  const [applicantPhones, setApplicantPhones] = useState<{
    countryCode: string;
    number: string;
  }[][]>([[{ countryCode: '+91', number: '' }]]);

  // Country codes list - comprehensive list
  const countryCodes = [
    { code: '+91', country: 'India' },
    { code: '+93', country: 'Afghanistan' },
    { code: '+355', country: 'Albania' },
    { code: '+213', country: 'Algeria' },
    { code: '+376', country: 'Andorra' },
    { code: '+244', country: 'Angola' },
    { code: '+54', country: 'Argentina' },
    { code: '+374', country: 'Armenia' },
    { code: '+61', country: 'Australia' },
    { code: '+43', country: 'Austria' },
    { code: '+994', country: 'Azerbaijan' },
    { code: '+973', country: 'Bahrain' },
    { code: '+880', country: 'Bangladesh' },
    { code: '+375', country: 'Belarus' },
    { code: '+32', country: 'Belgium' },
    { code: '+501', country: 'Belize' },
    { code: '+229', country: 'Benin' },
    { code: '+975', country: 'Bhutan' },
    { code: '+591', country: 'Bolivia' },
    { code: '+387', country: 'Bosnia' },
    { code: '+267', country: 'Botswana' },
    { code: '+55', country: 'Brazil' },
    { code: '+673', country: 'Brunei' },
    { code: '+359', country: 'Bulgaria' },
    { code: '+226', country: 'Burkina Faso' },
    { code: '+257', country: 'Burundi' },
    { code: '+855', country: 'Cambodia' },
    { code: '+237', country: 'Cameroon' },
    { code: '+1', country: 'Canada' },
    { code: '+238', country: 'Cape Verde' },
    { code: '+236', country: 'Central African Republic' },
    { code: '+235', country: 'Chad' },
    { code: '+56', country: 'Chile' },
    { code: '+86', country: 'China' },
    { code: '+57', country: 'Colombia' },
    { code: '+269', country: 'Comoros' },
    { code: '+242', country: 'Congo' },
    { code: '+506', country: 'Costa Rica' },
    { code: '+385', country: 'Croatia' },
    { code: '+53', country: 'Cuba' },
    { code: '+357', country: 'Cyprus' },
    { code: '+420', country: 'Czech Republic' },
    { code: '+45', country: 'Denmark' },
    { code: '+253', country: 'Djibouti' },
    { code: '+593', country: 'Ecuador' },
    { code: '+20', country: 'Egypt' },
    { code: '+503', country: 'El Salvador' },
    { code: '+240', country: 'Equatorial Guinea' },
    { code: '+291', country: 'Eritrea' },
    { code: '+372', country: 'Estonia' },
    { code: '+251', country: 'Ethiopia' },
    { code: '+679', country: 'Fiji' },
    { code: '+358', country: 'Finland' },
    { code: '+33', country: 'France' },
    { code: '+241', country: 'Gabon' },
    { code: '+220', country: 'Gambia' },
    { code: '+995', country: 'Georgia' },
    { code: '+49', country: 'Germany' },
    { code: '+233', country: 'Ghana' },
    { code: '+30', country: 'Greece' },
    { code: '+502', country: 'Guatemala' },
    { code: '+224', country: 'Guinea' },
    { code: '+245', country: 'Guinea-Bissau' },
    { code: '+592', country: 'Guyana' },
    { code: '+509', country: 'Haiti' },
    { code: '+504', country: 'Honduras' },
    { code: '+852', country: 'Hong Kong' },
    { code: '+36', country: 'Hungary' },
    { code: '+354', country: 'Iceland' },
    { code: '+91', country: 'India' },
    { code: '+62', country: 'Indonesia' },
    { code: '+98', country: 'Iran' },
    { code: '+964', country: 'Iraq' },
    { code: '+353', country: 'Ireland' },
    { code: '+972', country: 'Israel' },
    { code: '+39', country: 'Italy' },
    { code: '+225', country: 'Ivory Coast' },
    { code: '+81', country: 'Japan' },
    { code: '+962', country: 'Jordan' },
    { code: '+7', country: 'Kazakhstan' },
    { code: '+254', country: 'Kenya' },
    { code: '+965', country: 'Kuwait' },
    { code: '+996', country: 'Kyrgyzstan' },
    { code: '+856', country: 'Laos' },
    { code: '+371', country: 'Latvia' },
    { code: '+961', country: 'Lebanon' },
    { code: '+266', country: 'Lesotho' },
    { code: '+231', country: 'Liberia' },
    { code: '+218', country: 'Libya' },
    { code: '+423', country: 'Liechtenstein' },
    { code: '+370', country: 'Lithuania' },
    { code: '+352', country: 'Luxembourg' },
    { code: '+853', country: 'Macau' },
    { code: '+261', country: 'Madagascar' },
    { code: '+265', country: 'Malawi' },
    { code: '+60', country: 'Malaysia' },
    { code: '+960', country: 'Maldives' },
    { code: '+223', country: 'Mali' },
    { code: '+356', country: 'Malta' },
    { code: '+222', country: 'Mauritania' },
    { code: '+230', country: 'Mauritius' },
    { code: '+52', country: 'Mexico' },
    { code: '+373', country: 'Moldova' },
    { code: '+377', country: 'Monaco' },
    { code: '+976', country: 'Mongolia' },
    { code: '+382', country: 'Montenegro' },
    { code: '+212', country: 'Morocco' },
    { code: '+258', country: 'Mozambique' },
    { code: '+95', country: 'Myanmar' },
    { code: '+264', country: 'Namibia' },
    { code: '+977', country: 'Nepal' },
    { code: '+31', country: 'Netherlands' },
    { code: '+64', country: 'New Zealand' },
    { code: '+505', country: 'Nicaragua' },
    { code: '+227', country: 'Niger' },
    { code: '+234', country: 'Nigeria' },
    { code: '+47', country: 'Norway' },
    { code: '+968', country: 'Oman' },
    { code: '+92', country: 'Pakistan' },
    { code: '+970', country: 'Palestine' },
    { code: '+507', country: 'Panama' },
    { code: '+675', country: 'Papua New Guinea' },
    { code: '+595', country: 'Paraguay' },
    { code: '+51', country: 'Peru' },
    { code: '+63', country: 'Philippines' },
    { code: '+48', country: 'Poland' },
    { code: '+351', country: 'Portugal' },
    { code: '+974', country: 'Qatar' },
    { code: '+40', country: 'Romania' },
    { code: '+7', country: 'Russia' },
    { code: '+250', country: 'Rwanda' },
    { code: '+966', country: 'Saudi Arabia' },
    { code: '+221', country: 'Senegal' },
    { code: '+381', country: 'Serbia' },
    { code: '+248', country: 'Seychelles' },
    { code: '+232', country: 'Sierra Leone' },
    { code: '+65', country: 'Singapore' },
    { code: '+421', country: 'Slovakia' },
    { code: '+386', country: 'Slovenia' },
    { code: '+252', country: 'Somalia' },
    { code: '+27', country: 'South Africa' },
    { code: '+82', country: 'South Korea' },
    { code: '+211', country: 'South Sudan' },
    { code: '+34', country: 'Spain' },
    { code: '+94', country: 'Sri Lanka' },
    { code: '+249', country: 'Sudan' },
    { code: '+597', country: 'Suriname' },
    { code: '+268', country: 'Swaziland' },
    { code: '+46', country: 'Sweden' },
    { code: '+41', country: 'Switzerland' },
    { code: '+963', country: 'Syria' },
    { code: '+886', country: 'Taiwan' },
    { code: '+992', country: 'Tajikistan' },
    { code: '+255', country: 'Tanzania' },
    { code: '+66', country: 'Thailand' },
    { code: '+228', country: 'Togo' },
    { code: '+216', country: 'Tunisia' },
    { code: '+90', country: 'Turkey' },
    { code: '+993', country: 'Turkmenistan' },
    { code: '+256', country: 'Uganda' },
    { code: '+380', country: 'Ukraine' },
    { code: '+971', country: 'UAE' },
    { code: '+44', country: 'UK' },
    { code: '+1', country: 'USA' },
    { code: '+598', country: 'Uruguay' },
    { code: '+998', country: 'Uzbekistan' },
    { code: '+58', country: 'Venezuela' },
    { code: '+84', country: 'Vietnam' },
    { code: '+967', country: 'Yemen' },
    { code: '+260', country: 'Zambia' },
    { code: '+263', country: 'Zimbabwe' },
  ];

  // Merge phone numbers into single string for storage
  const mergePhoneNumbers = (phones: { countryCode: string; number: string }[]) => {
    return phones
      .filter(p => p.number.trim())
      .map(p => `${p.countryCode}-${p.number}`)
      .join(',');
  };

  // Unmerge phone number string into array for editing
  const unmergePhoneNumbers = (phoneString: string) => {
    if (!phoneString || !phoneString.trim()) {
      return [{ countryCode: '+91', number: '' }];
    }
    
    // Handle both formats:
    // Old format: "9377727251 / 9023122672" (slash or comma separated, no country codes)
    // New format: "+91-9377727251,+91-9023122672" (comma separated with country codes)
    const parts = phoneString.split(/[,\/]/).map(p => p.trim()).filter(p => p);
    
    return parts.map(part => {
      // Check if part contains country code (starts with + or has dash)
      if (part.includes('-')) {
        const [code, ...numParts] = part.split('-');
        return {
          countryCode: code.startsWith('+') ? code : '+91',
          number: numParts.join('-') // Handle numbers with dashes
        };
      }
      // Old format: just the number without country code
      return { countryCode: '+91', number: part };
    });
  };

  useEffect(() => {
    if (!bookingId) {
      navigate("/bookings");
      return;
    }
    loadBookingData();
  }, [bookingId]);

  useEffect(() => {
    loadInitialData();
  }, []);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowUnitDropdown(false);
        setShowTransferUnitDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const loadInitialData = async () => {
    try {
      const [projectsData, brokersData] = await Promise.all([
        projectService.getProjects(),
        brokerService.getBrokers({ page: 1, limit: 100 }),
      ]);
      setProjects(projectsData);
      setBrokers(brokersData.brokers);
    } catch (err) {
      setError("Failed to load initial data");
    }
  };

  const loadBookingData = async () => {
    try {
      setLoading(true);
      const bookingData = await bookingService.getBookingById(
        parseInt(bookingId!)
      );
      setBooking(bookingData);
      setSelectedUnit(bookingData.unit);
      setUnitSearchTerm(
        formatUnitDisplay(bookingData.unit.wing, bookingData.unit.unitNo)
      );
      setPaymentPlans(bookingData.paymentPlans || []);

      // Load brokers for this booking's project
      if (bookingData.projectId) {
        const brokersData = await brokerService.getBrokers({ 
          page: 1, 
          limit: 1000,
          projectId: bookingData.projectId 
        });
        setBrokers(brokersData.brokers);
      }

      // Map legacy payment terms to current values
      const mapPaymentTerms = (terms: string | null | undefined): string => {
        if (!terms || terms.trim() === "") {
          return "installments"; // Default
        }
        const normalized = terms.toLowerCase().trim();
        
        // Map legacy values to current radio button values
        if (normalized.includes("loan")) return "loan";
        if (normalized.includes("down")) return "down_payment";
        if (normalized.includes("installment")) return "installments";
        if (normalized.includes("mixed")) return "mixed_payment";
        
        // If no match, default to installments
        return "installments";
      };

      // Populate form data
      setFormData({
        brokerId: bookingData.brokerId,
        customerAddress: bookingData.customerAddress,
        remarks: bookingData.remarks,
        source: bookingData.source,
        paymentTerms: mapPaymentTerms(bookingData.paymentTerms),
        saleDeedAmount: bookingData.saleDeedAmount,
        chargesAmount: bookingData.chargesAmount,
        otherWorkAmount: bookingData.otherWorkAmount,
        terraceAmount: bookingData.terraceAmount,
        banakhatAmount: bookingData.banakhatAmount,
        applicants: bookingData.applicants,
      });

      // Initialize phone arrays from existing applicants
      if (bookingData.applicants && bookingData.applicants.length > 0) {
        const phoneArrays = bookingData.applicants.map(app => 
          unmergePhoneNumbers(app.contactNumber || '')
        );
        setApplicantPhones(phoneArrays);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load booking data");
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableUnits = async (projectId: number) => {
    try {
      const units = await bookingService.getUnsoldUnits(projectId);
      // Include the current unit even if it's sold
      if (booking?.unit && !units.find((u) => u.id === booking.unit.id)) {
        units.unshift(booking.unit);
      }
      setAvailableUnits(units);
    } catch (err) {
      console.error("Failed to load available units:", err);
    }
  };

  // Filter units based on search term - STANDARDIZED SEARCH
  const filteredUnits = availableUnits.filter((unit) => {
    // Use standardized unit search for exact "B-101" or "101" matching
    const unitMatches = filterUnitsBySearch([unit], unitSearchTerm).length > 0;

    // Also include additional filtering for unit type and size
    const typeMatches = unit.unitType
      .toLowerCase()
      .includes(unitSearchTerm.toLowerCase());
    const sizeMatches = unit.size
      .toLowerCase()
      .includes(unitSearchTerm.toLowerCase());

    return unitMatches || typeMatches || sizeMatches;
  });

  const handleUnitSelect = (unit: Unit) => {
    setSelectedUnit(unit);
    setUnitSearchTerm(formatUnitDisplay(unit.wing, unit.unitNo));
    setShowUnitDropdown(false);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.saleDeedAmount || formData.saleDeedAmount <= 0) {
      errors.saleDeedAmount = "Sale deed amount is required";
    }

    // Other work amount is mandatory
    if (
      formData.otherWorkAmount === undefined ||
      formData.otherWorkAmount === null
    ) {
      errors.otherWorkAmount = "Other work amount is required";
    }

    // Validate applicants
    formData.applicants?.forEach((applicant, index) => {
      if (!applicant.firstName.trim()) {
        errors[`applicant_${index}_firstName`] = "First name is required";
      }
      if (!applicant.surname.trim()) {
        errors[`applicant_${index}_surname`] = "Surname is required";
      }
      if (!applicant.contactNumber.trim()) {
        errors[`applicant_${index}_contactNumber`] =
          "Contact number is required";
      } else {
        const phones = unmergePhoneNumbers(applicant.contactNumber);
        const invalidPhone = phones.find(p => {
          if (!p.number) return false;
          if (p.countryCode === '+91') return p.number.length !== 10;
          return p.number.length < 4;
        });
        if (invalidPhone) {
          errors[`applicant_${index}_contactNumber`] = invalidPhone.countryCode === '+91'
            ? "Indian phone number must be exactly 10 digits"
            : "Phone number must be at least 4 digits";
        }
      }
    });

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      const errorMessages = Object.values(errors);
      toast.error(errorMessages.join('\n'), { style: { whiteSpace: 'pre-line' } });
    }
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !booking) return;

    // Debug: Log authorization status
    console.log('🔍 Edit reason check:', {
      bookingId: booking.id,
      status: booking.unit?.currentStatus,
      isSuperAdmin,
      isBooked: booking.unit?.currentStatus === 'BOOKED',
      shouldShowDialog: booking.unit?.currentStatus === 'BOOKED' && !isSuperAdmin
    });

    // Show edit reason dialog for BOOKED units (unless super admin)
    if (booking.unit?.currentStatus === 'BOOKED' && !isSuperAdmin) {
      console.log('✅ Showing edit reason dialog (unit status is BOOKED)');
      setEditReasonDialog((prev) => ({ ...prev, isOpen: true }));
      return;
    }

    // Otherwise proceed with update
    console.log('⏭️ Proceeding with direct update (no dialog needed)');
    await performUpdate();
  };

  const performUpdate = async () => {
    if (!booking) return;

    try {
      setLoading(true);
      
      // Include edit reason if provided (regardless of authorization status)
      const updateData = {
        ...formData,
        ...(editReasonDialog.editReason
          ? {
              editReason: editReasonDialog.editReason,
              editDetails:
                editReasonDialog.editReason === "Other"
                  ? editReasonDialog.editDetails
                  : undefined,
            }
          : {}),
      };

      await bookingService.updateBooking(booking.id, updateData);
      setSuccess("Booking updated successfully!");

      // Close edit reason dialog if open
      setEditReasonDialog({
        isOpen: false,
        editReason: "",
        editDetails: "",
        error: "",
      });

      // Redirect to bookings list after 2 seconds
      setTimeout(() => {
        navigate("/bookings");
      }, 2000);
    } catch (err: any) {
      if (err.response?.data?.requiresEditReason) {
        setEditReasonDialog((prev) => ({ ...prev, isOpen: true }));
      } else {
        setError(err.response?.data?.message || "Failed to update booking");
        toast.error(err.response?.data?.message || "Failed to update booking");
      }
    } finally {
      setLoading(false);
    }
  };

  const addApplicant = () => {
    setFormData((prev) => ({
      ...prev,
      applicants: [
        ...(prev.applicants || []),
        {
          firstName: "",
          fatherHusbandName: "",
          surname: "",
          panCard: "",
          aadharCard: "",
          contactNumber: "",
          isPrimary: false,
        },
      ],
    }));
    // Add phone array for new applicant
    setApplicantPhones(prev => [...prev, [{ countryCode: '+91', number: '' }]]);
  };

  const removeApplicant = (index: number) => {
    if ((formData.applicants?.length || 0) > 1) {
      setFormData((prev) => ({
        ...prev,
        applicants: prev.applicants?.filter((_, i) => i !== index),
      }));
      // Remove phone array for removed applicant
      setApplicantPhones(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateApplicant = (
    index: number,
    field: keyof BookingApplicant,
    value: any
  ) => {
    setFormData((prev) => ({
      ...prev,
      applicants: prev.applicants?.map((applicant, i) => {
        if (i === index) {
          const updated = { ...applicant, [field]: value };
          // If updating any phone-related field, merge the phone numbers
          if (field === 'contactNumber') {
            updated.contactNumber = mergePhoneNumbers(applicantPhones[index] || [{ countryCode: '+91', number: '' }]);
          }
          return updated;
        }
        return applicant;
      }),
    }));
  };

  // Update phone number for specific applicant and phone index
  const updateApplicantPhone = (applicantIndex: number, phoneIndex: number, field: 'countryCode' | 'number', value: string) => {
    setApplicantPhones(prev => {
      const updated = [...prev];
      if (!updated[applicantIndex]) {
        updated[applicantIndex] = [{ countryCode: '+91', number: '' }];
      }
      updated[applicantIndex] = [...updated[applicantIndex]];
      updated[applicantIndex][phoneIndex] = {
        ...updated[applicantIndex][phoneIndex],
        [field]: value
      };
      
      // Update the merged contactNumber in formData
      setFormData(prevForm => ({
        ...prevForm,
        applicants: prevForm.applicants?.map((app, i) => 
          i === applicantIndex 
            ? { ...app, contactNumber: mergePhoneNumbers(updated[applicantIndex]) }
            : app
        ),
      }));
      
      return updated;
    });
  };

  // Add another phone number for applicant (max 2)
  const addPhoneNumber = (applicantIndex: number) => {
    setApplicantPhones(prev => {
      const updated = [...prev];
      if (updated[applicantIndex].length < 2) {
        updated[applicantIndex] = [...updated[applicantIndex], { countryCode: '+91', number: '' }];
      }
      return updated;
    });
  };

  // Remove phone number for applicant
  const removePhoneNumber = (applicantIndex: number, phoneIndex: number) => {
    setApplicantPhones(prev => {
      const updated = [...prev];
      updated[applicantIndex] = updated[applicantIndex].filter((_, i) => i !== phoneIndex);
      
      // Ensure at least one phone field remains
      if (updated[applicantIndex].length === 0) {
        updated[applicantIndex] = [{ countryCode: '+91', number: '' }];
      }
      
      // Update merged contactNumber
      setFormData(prevForm => ({
        ...prevForm,
        applicants: prevForm.applicants?.map((app, i) => 
          i === applicantIndex 
            ? { ...app, contactNumber: mergePhoneNumbers(updated[applicantIndex]) }
            : app
        ),
      }));
      
      return updated;
    });
  };

  const handleCancelBooking = () => {
    setCancelModal({ ...cancelModal, isOpen: true });
  };

  const handleDeleteBooking = () => {
    setConfirmDialog({
      isOpen: true,
      type: "delete",
      title: "Delete Booking",
      message:
        "Are you sure you want to permanently delete this booking? This action cannot be undone.",
      onConfirm: () => {
        setPasswordDialog({ isOpen: true, password: "", error: "" });
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleTransferBooking = async () => {
    // Load available units for transfer only when modal opens
    if (booking?.projectId) {
      await loadAvailableUnits(booking.projectId);
    }

    setTransferModal({
      ...transferModal,
      isOpen: true,
      newAmounts: {
        saleDeedAmount: booking?.saleDeedAmount || 0,
        chargesAmount: booking?.chargesAmount || 0,
        otherWorkAmount: booking?.otherWorkAmount || 0,
        terraceAmount: booking?.terraceAmount || 0,
        banakhatAmount: booking?.banakhatAmount || 0,
      },
    });
  };

  const handlePasswordConfirm = async () => {
    if (!passwordDialog.password.trim()) {
      setPasswordDialog((prev) => ({ ...prev, error: "Password is required" }));
      return;
    }

    try {
      setLoading(true);

      if (confirmDialog.type === "cancel") {
        await bookingService.cancelBooking(booking!.id, {
          reason: cancelModal.reason,
          refundAmount: 0,
        });
        setSuccess("Booking cancelled successfully!");
        setTimeout(() => navigate("/bookings"), 2000);
      } else if (confirmDialog.type === "delete") {
        await bookingService.deleteBooking(
          booking!.id,
          passwordDialog.password
        );
        setSuccess("Booking deleted successfully!");
        setTimeout(() => navigate("/bookings"), 2000);
      } else if (confirmDialog.type === "transfer") {
        await bookingService.transferBooking(booking!.id, {
          transferType: transferModal.transferType,
          newUnitId:
            transferModal.transferType === "unit"
              ? transferModal.newUnitId
              : undefined,
          newApplicants:
            transferModal.transferType === "name"
              ? transferModal.newApplicants
              : undefined,
          newAmounts: transferModal.newAmounts,
          reason: transferModal.reason,
          password: passwordDialog.password,
        });
        setSuccess("Booking transferred successfully!");
        await loadBookingData();
      }

      // Close modals
      setPasswordDialog({ isOpen: false, password: "", error: "" });
      setCancelModal({ isOpen: false, reason: "", error: "" });
      setTransferModal({
        isOpen: false,
        transferType: "name",
        newUnitId: 0,
        newApplicants: [
          {
            firstName: "",
            fatherHusbandName: "",
            surname: "",
            contactNumber: "",
            isPrimary: true,
          },
        ],
        newAmounts: {
          saleDeedAmount: 0,
          chargesAmount: 0,
          otherWorkAmount: 0,
          terraceAmount: 0,
          banakhatAmount: 0,
        },
        reason: "",
        error: "",
      });
    } catch (err: any) {
      setPasswordDialog((prev) => ({
        ...prev,
        error: err.response?.data?.message || "Action failed",
      }));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const formatHiddenIncome = (amount: number): string => {
    return `₹${amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  if (loading && !booking) {
    return (
      <div className="add-booking-page">
        <div className="loading">Loading booking data...</div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="add-booking-page">
        <div className="error-message">Booking not found</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header" style={{ padding: '12px 20px', minHeight: 'auto' }}>
        <h2 style={{ color: "#1a1a1a", margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
          Edit Booking - {booking.bookingId}
        </h2>
        <div className="header-actions">
          <button
            className="btn btn-secondary"
            onClick={() => navigate("/bookings")}
          >
            Back to Bookings
          </button>
          <button
            className="btn btn-warning"
            onClick={handleTransferBooking}
            disabled={loading}
          >
            <FaExchangeAlt /> Transfer Booking
          </button>
          <button
            className="btn btn-danger"
            onClick={handleCancelBooking}
            disabled={loading}
          >
            <FaTimes /> Cancel Booking
          </button>
          {/* Delete button: Show only if booking not approved OR user is superadmin */}
          {(!booking?.authorizedAt || isSuperAdmin) && (
            <button
              className="btn btn-danger"
              onClick={handleDeleteBooking}
              disabled={loading}
            >
              <FaTrash /> Delete Booking
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={{ display: 'none' }}>
          {error}
        </div>
      )}

      {success && <div className="success-message">{success}</div>}

      <div className="booking-form-container">
        <form onSubmit={handleSubmit} className="booking-form">
          {/* Booking Details Section - Read Only */}
          <div className="form-section compact-section">
            <h3>
              <FaCalendar /> Booking Details (Read Only)
            </h3>

            <div className="form-row compact-row">
              <div className="form-group">
                <label>Project</label>
                <input
                  type="text"
                  value={
                    booking.unit?.project?.name ||
                    booking.project?.name ||
                    "N/A"
                  }
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label>Unit</label>
                <input
                  type="text"
                  value={formatUnitDisplay(
                    booking.unit.wing,
                    booking.unit.unitNo
                  )}
                  disabled
                  readOnly
                />
              </div>

              <div className="form-group">
                <label>Booking Date</label>
                <input
                  type="text"
                  value={new Date(booking.bookingDate).toLocaleDateString()}
                  disabled
                  readOnly
                />
              </div>
            </div>
          </div>

          {/* Unit Details (Read-only) */}
          <div className="form-section unit-details compact-section">
            <h3>
              <FaHome /> Unit Details
            </h3>
            <div className="unit-details-table">
              <div className="unit-details-row">
                <div className="unit-detail-cell">
                  <label>Unit Number:</label>
                  <span>
                    {formatUnitDisplay(selectedUnit?.wing, selectedUnit?.unitNo)}
                  </span>
                </div>
                <div className="unit-detail-cell">
                  <label>Unit Type:</label>
                  <span>{selectedUnit?.unitType}</span>
                </div>
                <div className="unit-detail-cell">
                  <label>Size:</label>
                  <span>{selectedUnit?.size}</span>
                </div>
                <div className="unit-detail-cell">
                  <label>Wing:</label>
                  <span>{selectedUnit?.wing}</span>
                </div>
                <div className="unit-detail-cell">
                  <label>Sale Deed Amount:</label>
                  <span className="amount">
                    {formatCurrency(selectedUnit?.saleDeedAmount || 0)}
                  </span>
                </div>
                <div className="unit-detail-cell">
                  <label>Terrace:</label>
                  <span>{selectedUnit?.terraceStatus || "No"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Customer Details */}
          <div className="form-section compact-section">
            <h3>
              <FaUser /> Customer Details
            </h3>

            {formData.applicants?.map((applicant, index) => (
              <div key={index} className="applicant-section compact-applicant applicant-card">
                <div className="applicant-header">
                  <h4>Applicant {index + 1}</h4>
                  {(formData.applicants?.length || 0) > 1 && (
                    <button
                      type="button"
                      className="btn-danger btn-small"
                      onClick={() => removeApplicant(index)}
                      disabled={loading}
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>

                {/* All fields in one row: First Name, Father/Husband, Surname, PAN, Aadhar */}
                <div className="form-row compact-row applicant-details-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={applicant.firstName}
                      onChange={(e) =>
                        updateApplicant(index, "firstName", e.target.value)
                      }
                      disabled={loading}
                    />
                    {formErrors[`applicant_${index}_firstName`] && (
                      <span className="error">
                        {formErrors[`applicant_${index}_firstName`]}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Father/Husband Name</label>
                    <input
                      type="text"
                      value={applicant.fatherHusbandName}
                      onChange={(e) =>
                        updateApplicant(
                          index,
                          "fatherHusbandName",
                          e.target.value
                        )
                      }
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group">
                    <label>Surname *</label>
                    <input
                      type="text"
                      value={applicant.surname}
                      onChange={(e) =>
                        updateApplicant(index, "surname", e.target.value)
                      }
                      disabled={loading}
                    />
                    {formErrors[`applicant_${index}_surname`] && (
                      <span className="error">
                        {formErrors[`applicant_${index}_surname`]}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>PAN Number</label>
                    <input
                      type="text"
                      value={applicant.panCard || ""}
                      onChange={(e) =>
                        updateApplicant(index, "panCard", e.target.value.toUpperCase())
                      }
                      placeholder="ABCDE1234F"
                      maxLength={10}
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group">
                    <label>Aadhar Number</label>
                    <input
                      type="text"
                      value={applicant.aadharCard || ""}
                      onChange={(e) =>
                        updateApplicant(index, "aadharCard", e.target.value.replace(/\D/g, ''))
                      }
                      placeholder="1234 5678 9012"
                      maxLength={12}
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Phone Numbers with Country Code */}
                <div className="phone-numbers-section">
                  <label className="section-label">Contact Numbers *</label>
                  {(applicantPhones[index] && applicantPhones[index].length > 0 ? applicantPhones[index] : [{ countryCode: '+91', number: '' }]).map((phone, phoneIndex) => (
                    <div key={phoneIndex} className="phone-input-group">
                      <select
                        value={phone.countryCode}
                        onChange={(e) =>
                          updateApplicantPhone(index, phoneIndex, 'countryCode', e.target.value)
                        }
                        disabled={loading}
                        className="country-code-select"
                      >
                        {countryCodes.map(cc => (
                          <option key={cc.code} value={cc.code}>
                            {cc.code} ({cc.country})
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={phone.number}
                        onChange={(e) =>
                          updateApplicantPhone(index, phoneIndex, 'number', e.target.value.replace(/\D/g, ''))
                        }
                        placeholder="Enter phone number"
                        maxLength={phone.countryCode === '+91' ? 10 : 15}
                        disabled={loading}
                        className="phone-number-input"
                      />
                      {phoneIndex === 0 && (applicantPhones[index]?.length || 1) < 2 && (
                        <button
                          type="button"
                          className="btn-icon add-phone"
                          onClick={() => addPhoneNumber(index)}
                          disabled={loading}
                          title="Add another number"
                        >
                          <FaPlus />
                        </button>
                      )}
                      {phoneIndex > 0 && (
                        <button
                          type="button"
                          className="btn-icon remove-phone"
                          onClick={() => removePhoneNumber(index, phoneIndex)}
                          disabled={loading}
                          title="Remove this number"
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>
                  ))}
                  {formErrors[`applicant_${index}_contactNumber`] && (
                    <span className="error">
                      {formErrors[`applicant_${index}_contactNumber`]}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={applicant.isPrimary}
                      onChange={(e) => {
                        // If setting this as primary, unset all others
                        if (e.target.checked) {
                          setFormData((prev) => ({
                            ...prev,
                            applicants: prev.applicants?.map((app, i) => ({
                              ...app,
                              isPrimary: i === index,
                            })),
                          }));
                        } else {
                          updateApplicant(index, "isPrimary", false);
                        }
                      }}
                      disabled={loading}
                    />
                    Primary Applicant
                  </label>
                </div>
              </div>
            ))}

            <button
              type="button"
              className="btn btn-secondary btn-small"
              onClick={addApplicant}
              disabled={loading}
            >
              Add Applicant
            </button>

            {/* Customer Address - Full Width */}
            <div className="form-group">
              <label>Customer Address</label>
              <input
                type="text"
                value={formData.customerAddress || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    customerAddress: e.target.value,
                  }))
                }
                disabled={loading}
                placeholder="Enter customer address"
              />
            </div>

            {/* Remarks - Full Width */}
            {!hiddenFields.booking.remarks && (
            <div className="form-group">
              <label>Remarks</label>
              <input
                type="text"
                value={formData.remarks || ""}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, remarks: e.target.value }))
                }
                disabled={loading}
                placeholder="Enter remarks"
              />
            </div>
            )}
          </div>

          {/* Payment Terms */}
          <div className="form-section compact-section">
            {/* Payment Terms Selection */}
            <div className="form-group">
              <label>Payment Terms</label>
              <div className="radio-group" style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <label className={`radio-label ${formData.paymentTerms === "down_payment" ? "selected" : ""}`}>
                  <input
                    type="radio"
                    name="planType"
                    value="down_payment"
                    checked={formData.paymentTerms === "down_payment"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        paymentTerms: e.target.value,
                      }))
                    }
                    disabled={loading}
                  />
                  Down Payment
                </label>
                <label className={`radio-label ${formData.paymentTerms === "loan" ? "selected" : ""}`}>
                  <input
                    type="radio"
                    name="planType"
                    value="loan"
                    checked={formData.paymentTerms === "loan"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        paymentTerms: e.target.value,
                      }))
                    }
                    disabled={loading}
                  />
                  Loan
                </label>
                <label className={`radio-label ${formData.paymentTerms === "installments" ? "selected" : ""}`}>
                  <input
                    type="radio"
                    name="planType"
                    value="installments"
                    checked={formData.paymentTerms === "installments"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        paymentTerms: e.target.value,
                      }))
                    }
                    disabled={loading}
                  />
                  Installments
                </label>
                <label className={`radio-label ${formData.paymentTerms === "mixed_payment" ? "selected" : ""}`}>
                  <input
                    type="radio"
                    name="planType"
                    value="mixed_payment"
                    checked={formData.paymentTerms === "mixed_payment"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        paymentTerms: e.target.value,
                      }))
                    }
                    disabled={loading}
                  />
                  Mixed Payment
                </label>
              </div>
            </div>

            {/* All 5 amounts in one row */}
            <div className="form-row amounts-row">
              <div className="form-group">
                <label>Sale Deed Amount *</label>
                <input
                  type="number"
                  value={
                    formData.saleDeedAmount === 0 ? "" : formData.saleDeedAmount
                  }
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      saleDeedAmount: parseFloat(e.target.value) || 0,
                    }))
                  }
                  onWheel={(e) => e.currentTarget.blur()}
                  min={selectedUnit?.saleDeedAmount || 0}
                  disabled={loading}
                  placeholder="Enter sale deed amount"
                  required
                />
                {formErrors.saleDeedAmount && (
                  <span className="error">{formErrors.saleDeedAmount}</span>
                )}
              </div>

              {!hiddenFields.booking.otherWorkAmount && (
                <div className="form-group">
                  <label>Other Work Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={
                      formData.otherWorkAmount === 0
                        ? ""
                        : formData.otherWorkAmount
                    }
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        otherWorkAmount: parseFloat(e.target.value) || 0,
                      }))
                    }
                    onWheel={(e) => e.currentTarget.blur()}
                    min="0"
                    disabled={loading}
                    placeholder="Enter other work amount"
                    required
                  />
                  {formErrors.otherWorkAmount && (
                    <span className="error">{formErrors.otherWorkAmount}</span>
                  )}
                </div>
              )}

              {!hiddenFields.booking.terraceAmount && (
                <div className="form-group">
                  <label>Terrace Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={
                      formData.terraceAmount === 0 ? "" : formData.terraceAmount
                    }
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        terraceAmount: parseFloat(e.target.value) || 0,
                      }))
                    }
                    onWheel={(e) => e.currentTarget.blur()}
                    min="0"
                    disabled={
                      selectedUnit?.terraceStatus?.toLowerCase() !== "yes" ||
                      loading
                    }
                    placeholder="Enter terrace amount"
                  />
                </div>
              )}

              {!hiddenFields.booking.chargesAmount && (
                <div className="form-group">
                  <label>Extra Work Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={
                      formData.chargesAmount === 0 ? "" : formData.chargesAmount
                    }
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        chargesAmount: parseFloat(e.target.value) || 0,
                      }))
                    }
                    onWheel={(e) => e.currentTarget.blur()}
                    min="0"
                    disabled={loading}
                    placeholder="Enter extra work amount"
                  />
                </div>
              )}

              {!hiddenFields.booking.banakhatAmount && (
                <div className="form-group">
                  <label>Banakhat Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={
                      formData.banakhatAmount === 0
                        ? ""
                        : formData.banakhatAmount
                    }
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        banakhatAmount: parseFloat(e.target.value) || 0,
                      }))
                    }
                    onWheel={(e) => e.currentTarget.blur()}
                    min="0"
                    disabled={loading}
                    placeholder="Enter banakhat amount"
                  />
                </div>
              )}
            </div>

            <div className="total-amount">
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#059669",
                  }}
                >
                  Main: {formatCurrency(formData.saleDeedAmount || 0)}
                </div>
                {(!hiddenFields.booking.chargesAmount ||
                  !hiddenFields.booking.otherWorkAmount ||
                  !hiddenFields.booking.terraceAmount ||
                  !hiddenFields.booking.banakhatAmount) && (
                  <div style={{ fontSize: "14px", color: "#6b7280" }}>
                    Receivables:{" "}
                    {formatHiddenIncome(
                      (formData.chargesAmount || 0) +
                        (formData.otherWorkAmount || 0) +
                        (formData.terraceAmount || 0) +
                        (formData.banakhatAmount || 0)
                    )}
                  </div>
                )}
                {false && (
                  <div
                    style={{
                      borderTop: "2px solid #1976d2",
                      paddingTop: "8px",
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "#1565c0",
                    }}
                  >
                    Total:{" "}
                    {formatCurrency(
                      (formData.saleDeedAmount || 0) +
                        (formData.chargesAmount || 0) +
                        (formData.otherWorkAmount || 0) +
                        (formData.terraceAmount || 0) +
                        (formData.banakhatAmount || 0)
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate("/bookings")}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? "Updating..." : "Update Booking"}
            </button>
          </div>
        </form>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() =>
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }))
        }
      />

      {/* Password Confirmation Dialog */}
      {passwordDialog.isOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Action</h3>
              <button
                onClick={() =>
                  setPasswordDialog({ isOpen: false, password: "", error: "" })
                }
                className="close-button"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Please enter your password to confirm this action:</p>
              <input
                type="password"
                placeholder="Enter password"
                value={passwordDialog.password}
                onChange={(e) =>
                  setPasswordDialog((prev) => ({
                    ...prev,
                    password: e.target.value,
                    error: "",
                  }))
                }
                onKeyPress={(e) => e.key === "Enter" && handlePasswordConfirm()}
              />
              {passwordDialog.error && (
                <div className="error">{passwordDialog.error}</div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() =>
                  setPasswordDialog({ isOpen: false, password: "", error: "" })
                }
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handlePasswordConfirm}
                disabled={!passwordDialog.password}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Booking Modal */}
      {cancelModal.isOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Cancel Booking</h3>
              <button
                onClick={() =>
                  setCancelModal({ ...cancelModal, isOpen: false })
                }
                className="close-button"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="payment-terms-display">
                <h4>Current Payment Terms:</h4>
                <div className="payment-summary">
                  <div>
                    Sale Deed Amount:{" "}
                    {formatCurrency(booking?.saleDeedAmount || 0)}
                  </div>
                  <div>
                    Extra Work Amount:{" "}
                    {formatHiddenIncome(booking?.chargesAmount || 0)}
                  </div>
                  <div>
                    Other Work Amount:{" "}
                    {formatHiddenIncome(booking?.otherWorkAmount || 0)}
                  </div>
                  <div>
                    Terrace Amount:{" "}
                    {formatHiddenIncome(booking?.terraceAmount || 0)}
                  </div>
                  <div>
                    Banakhat Amount:{" "}
                    {formatHiddenIncome(booking?.banakhatAmount || 0)}
                  </div>
                  <div className="total">
                    Total: {formatCurrency(booking?.totalAmount || 0)}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Cancellation Reason *</label>
                <textarea
                  placeholder="Enter reason for cancellation"
                  value={cancelModal.reason}
                  onChange={(e) =>
                    setCancelModal((prev) => ({
                      ...prev,
                      reason: e.target.value,
                      error: "",
                    }))
                  }
                  rows={3}
                  required
                />
              </div>

              {cancelModal.error && (
                <div className="error">{cancelModal.error}</div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() =>
                  setCancelModal({ ...cancelModal, isOpen: false })
                }
              >
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={() => {
                  if (!cancelModal.reason.trim()) {
                    setCancelModal((prev) => ({
                      ...prev,
                      error: "Reason is required",
                    }));
                    return;
                  }
                  setConfirmDialog({
                    isOpen: false,
                    type: "cancel",
                    title: "Cancel Booking",
                    message: "",
                    onConfirm: () => {},
                  });
                  setCancelModal((prev) => ({ ...prev, isOpen: false }));
                  setPasswordDialog({ isOpen: true, password: "", error: "" });
                }}
                disabled={!cancelModal.reason.trim()}
              >
                Proceed to Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Booking Modal */}
      {transferModal.isOpen && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <div className="modal-header">
              <h3>Transfer Booking</h3>
              <button
                onClick={() =>
                  setTransferModal({ ...transferModal, isOpen: false })
                }
                className="close-button"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Transfer Type *</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="transferType"
                      value="name"
                      checked={transferModal.transferType === "name"}
                      onChange={(e) =>
                        setTransferModal((prev) => ({
                          ...prev,
                          transferType: e.target.value as "name" | "unit",
                        }))
                      }
                    />
                    Name Transfer
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="transferType"
                      value="unit"
                      checked={transferModal.transferType === "unit"}
                      onChange={(e) =>
                        setTransferModal((prev) => ({
                          ...prev,
                          transferType: e.target.value as "name" | "unit",
                        }))
                      }
                    />
                    Unit Transfer
                  </label>
                </div>
              </div>

              {transferModal.transferType === "unit" && (
                <>
                  <div className="form-group">
                    <label>New Unit *</label>
                    <div className="unit-search-container" ref={dropdownRef}>
                      <input
                        type="text"
                        placeholder="Search unit by wing-unit (e.g., A-101)"
                        value={transferUnitSearchTerm}
                        onChange={(e) => {
                          setTransferUnitSearchTerm(e.target.value);
                          setShowTransferUnitDropdown(true);
                        }}
                        onFocus={() => setShowTransferUnitDropdown(true)}
                      />
                      {showTransferUnitDropdown && (
                        <div className="unit-dropdown">
                          {availableUnits
                            .filter(
                              (unit) =>
                                unit.id !== booking?.unitId && // Exclude current unit
                                formatUnitDisplay(unit.wing, unit.unitNo)
                                  .toLowerCase()
                                  .includes(
                                    transferUnitSearchTerm.toLowerCase()
                                  )
                            )
                            .map((unit) => (
                              <div
                                key={unit.id}
                                className="unit-option"
                                onClick={() => {
                                  setTransferModal((prev) => ({
                                    ...prev,
                                    newUnitId: unit.id,
                                    newAmounts: {
                                      saleDeedAmount: unit.saleDeedAmount || 0,
                                      chargesAmount:
                                        booking?.chargesAmount || 0,
                                      otherWorkAmount:
                                        booking?.otherWorkAmount || 0,
                                      terraceAmount:
                                        booking?.terraceAmount || 0,
                                      banakhatAmount:
                                        booking?.banakhatAmount || 0,
                                    },
                                  }));
                                  setTransferUnitSearchTerm(
                                    formatUnitDisplay(unit.wing, unit.unitNo)
                                  );
                                  setShowTransferUnitDropdown(false);
                                }}
                              >
                                <div className="unit-main">
                                  {formatUnitDisplay(unit.wing, unit.unitNo)}
                                </div>
                                <div className="unit-details">
                                  {unit.unitType} • {unit.size} •{" "}
                                  {formatCurrency(unit.saleDeedAmount || 0)}
                                </div>
                              </div>
                            ))}
                          {availableUnits.filter(
                            (unit) =>
                              unit.id !== booking?.unitId &&
                              formatUnitDisplay(unit.wing, unit.unitNo)
                                .toLowerCase()
                                .includes(transferUnitSearchTerm.toLowerCase())
                          ).length === 0 && (
                            <div className="no-results">No units found</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {transferModal.transferType === "name" && (
                <div className="new-applicants-section">
                  <div className="applicants-header">
                    <h4>New Applicants</h4>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        setTransferModal((prev) => ({
                          ...prev,
                          newApplicants: [
                            ...prev.newApplicants,
                            {
                              firstName: "",
                              fatherHusbandName: "",
                              surname: "",
                              contactNumber: "",
                              isPrimary: false,
                            },
                          ],
                        }));
                      }}
                    >
                      + Add Applicant
                    </button>
                  </div>

                  {transferModal.newApplicants.map((applicant, index) => (
                    <div key={index} className="applicant-card">
                      <div className="applicant-header">
                        <span className="applicant-label">
                          Applicant {index + 1} {index === 0 && "(Primary)"}
                        </span>
                        {index > 0 && (
                          <button
                            type="button"
                            className="remove-applicant-btn"
                            onClick={() => {
                              const newApplicants =
                                transferModal.newApplicants.filter(
                                  (_, i) => i !== index
                                );
                              setTransferModal((prev) => ({
                                ...prev,
                                newApplicants,
                              }));
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>

                      <div className="applicant-form-grid">
                        <div className="form-group">
                          <label>First Name *</label>
                          <input
                            type="text"
                            placeholder="First Name"
                            value={applicant.firstName}
                            onChange={(e) => {
                              const newApplicants = [
                                ...transferModal.newApplicants,
                              ];
                              newApplicants[index].firstName = e.target.value;
                              setTransferModal((prev) => ({
                                ...prev,
                                newApplicants,
                              }));
                            }}
                          />
                        </div>

                        <div className="form-group">
                          <label>Father/Husband Name *</label>
                          <input
                            type="text"
                            placeholder="Father/Husband Name"
                            value={applicant.fatherHusbandName}
                            onChange={(e) => {
                              const newApplicants = [
                                ...transferModal.newApplicants,
                              ];
                              newApplicants[index].fatherHusbandName =
                                e.target.value;
                              setTransferModal((prev) => ({
                                ...prev,
                                newApplicants,
                              }));
                            }}
                          />
                        </div>

                        <div className="form-group">
                          <label>Surname *</label>
                          <input
                            type="text"
                            placeholder="Surname"
                            value={applicant.surname}
                            onChange={(e) => {
                              const newApplicants = [
                                ...transferModal.newApplicants,
                              ];
                              newApplicants[index].surname = e.target.value;
                              setTransferModal((prev) => ({
                                ...prev,
                                newApplicants,
                              }));
                            }}
                          />
                        </div>

                        <div className="form-group">
                          <label>Contact Number *</label>
                          <input
                            type="text"
                            placeholder="Contact Number"
                            value={applicant.contactNumber}
                            onChange={(e) => {
                              const newApplicants = [
                                ...transferModal.newApplicants,
                              ];
                              newApplicants[index].contactNumber =
                                e.target.value;
                              setTransferModal((prev) => ({
                                ...prev,
                                newApplicants,
                              }));
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Transfer Amounts Section */}
              <div className="transfer-amounts-section">
                <h4>Transfer Payment Details</h4>
                {transferModal.transferType === "unit" &&
                  transferModal.newUnitId > 0 && (
                    <div className="unit-base-price-info">
                      <small>
                        Selected Unit Base Price:{" "}
                        {formatCurrency(
                          availableUnits.find(
                            (u) => u.id === transferModal.newUnitId
                          )?.saleDeedAmount || 0
                        )}
                      </small>
                    </div>
                  )}
                <div className="amounts-grid">
                  <div className="form-group">
                    <label>Sale Deed Amount *</label>
                    <input
                      type="number"
                      value={
                        transferModal.newAmounts.saleDeedAmount === 0
                          ? ""
                          : transferModal.newAmounts.saleDeedAmount
                      }
                      onChange={(e) =>
                        setTransferModal((prev) => ({
                          ...prev,
                          newAmounts: {
                            ...prev.newAmounts,
                            saleDeedAmount: parseFloat(e.target.value) || 0,
                          },
                        }))
                      }
                      onWheel={(e) => e.currentTarget.blur()}
                      min="1"
                      step="0.01"
                      placeholder="Enter sale deed amount"
                    />
                    <small className="help-text">
                      You can negotiate this amount, but it should be reasonable
                      compared to the unit's base price.
                    </small>
                  </div>
                  {!hiddenFields.booking.chargesAmount && (
                    <div className="form-group">
                      <label>Extra Work Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        value={
                          transferModal.newAmounts.chargesAmount === 0
                            ? ""
                            : transferModal.newAmounts.chargesAmount
                        }
                        onChange={(e) =>
                          setTransferModal((prev) => ({
                            ...prev,
                            newAmounts: {
                              ...prev.newAmounts,
                              chargesAmount: parseFloat(e.target.value) || 0,
                            },
                          }))
                        }
                        onWheel={(e) => e.currentTarget.blur()}
                        min="0"
                        placeholder="Enter changes amount"
                      />
                    </div>
                  )}
                  {!hiddenFields.booking.otherWorkAmount && (
                    <div className="form-group">
                      <label>Other Work Amount *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={
                          transferModal.newAmounts.otherWorkAmount === 0
                            ? ""
                            : transferModal.newAmounts.otherWorkAmount
                        }
                        onChange={(e) =>
                          setTransferModal((prev) => ({
                            ...prev,
                            newAmounts: {
                              ...prev.newAmounts,
                              otherWorkAmount: parseFloat(e.target.value) || 0,
                            },
                          }))
                        }
                        onWheel={(e) => e.currentTarget.blur()}
                        min="0"
                        placeholder="Enter other work amount"
                      />
                    </div>
                  )}
                  {!hiddenFields.booking.terraceAmount && (
                    <div className="form-group">
                      <label>Terrace Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        value={
                          transferModal.newAmounts.terraceAmount === 0
                            ? ""
                            : transferModal.newAmounts.terraceAmount
                        }
                        onChange={(e) =>
                          setTransferModal((prev) => ({
                            ...prev,
                            newAmounts: {
                              ...prev.newAmounts,
                              terraceAmount: parseFloat(e.target.value) || 0,
                            },
                          }))
                        }
                        onWheel={(e) => e.currentTarget.blur()}
                        min="0"
                        placeholder="Enter terrace amount"
                      />
                    </div>
                  )}

                  {!hiddenFields.booking.banakhatAmount && (
                    <div className="form-group">
                      <label>Banakhat Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        value={
                          transferModal.newAmounts.banakhatAmount === 0
                            ? ""
                            : transferModal.newAmounts.banakhatAmount
                        }
                        onChange={(e) =>
                          setTransferModal((prev) => ({
                            ...prev,
                            newAmounts: {
                              ...prev.newAmounts,
                              banakhatAmount: parseFloat(e.target.value) || 0,
                            },
                          }))
                        }
                        onWheel={(e) => e.currentTarget.blur()}
                        min="0"
                        placeholder="Enter banakhat amount"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Transfer Summary */}
              <div
                className="transfer-summary"
                style={{
                  background: "#f8f9fa",
                  border: "1px solid #dee2e6",
                  borderRadius: "8px",
                  padding: "16px",
                  margin: "16px 0",
                }}
              >
                <h4
                  style={{
                    margin: "0 0 12px 0",
                    color: "#495057",
                    fontSize: "16px",
                  }}
                >
                  Transfer Summary
                </h4>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#059669",
                    }}
                  >
                    Main:{" "}
                    {formatCurrency(
                      transferModal.newAmounts.saleDeedAmount || 0
                    )}
                  </div>
                  {(!hiddenFields.booking.chargesAmount ||
                    !hiddenFields.booking.otherWorkAmount ||
                    !hiddenFields.booking.terraceAmount) && (
                    <div style={{ fontSize: "14px", color: "#6b7280" }}>
                      Receivables:{" "}
                      {formatHiddenIncome(
                        (!hiddenFields.booking.chargesAmount
                          ? transferModal.newAmounts.chargesAmount || 0
                          : 0) +
                          (!hiddenFields.booking.otherWorkAmount
                            ? transferModal.newAmounts.otherWorkAmount || 0
                            : 0) +
                          (!hiddenFields.booking.terraceAmount
                            ? transferModal.newAmounts.terraceAmount || 0
                            : 0)
                      )}
                    </div>
                  )}
                  <div
                    style={{
                      borderTop: "2px solid #1976d2",
                      paddingTop: "8px",
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "#1565c0",
                    }}
                  >
                    Total:{" "}
                    {formatCurrency(
                      (transferModal.newAmounts.saleDeedAmount || 0) +
                        (!hiddenFields.booking.chargesAmount
                          ? transferModal.newAmounts.chargesAmount || 0
                          : 0) +
                        (!hiddenFields.booking.otherWorkAmount
                          ? transferModal.newAmounts.otherWorkAmount || 0
                          : 0) +
                        (!hiddenFields.booking.terraceAmount
                          ? transferModal.newAmounts.terraceAmount || 0
                          : 0)
                    )}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Transfer Reason *</label>
                <textarea
                  placeholder="Enter reason for transfer"
                  value={transferModal.reason}
                  onChange={(e) =>
                    setTransferModal((prev) => ({
                      ...prev,
                      reason: e.target.value,
                      error: "",
                    }))
                  }
                  rows={3}
                  required
                />
              </div>

              {transferModal.error && (
                <div className="error">{transferModal.error}</div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() =>
                  setTransferModal({ ...transferModal, isOpen: false })
                }
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (!transferModal.reason.trim()) {
                    setTransferModal((prev) => ({
                      ...prev,
                      error: "Reason is required",
                    }));
                    return;
                  }
                  if (
                    transferModal.transferType === "unit" &&
                    !transferModal.newUnitId
                  ) {
                    setTransferModal((prev) => ({
                      ...prev,
                      error: "New unit is required",
                    }));
                    return;
                  }
                  if (
                    transferModal.transferType === "name" &&
                    transferModal.newApplicants.some(
                      (a) => !a.firstName.trim() || !a.surname.trim()
                    )
                  ) {
                    setTransferModal((prev) => ({
                      ...prev,
                      error: "All applicant names are required",
                    }));
                    return;
                  }
                  setConfirmDialog({
                    isOpen: false,
                    type: "transfer",
                    title: "Transfer Booking",
                    message: "",
                    onConfirm: () => {},
                  });
                  setTransferModal((prev) => ({ ...prev, isOpen: false }));
                  setPasswordDialog({ isOpen: true, password: "", error: "" });
                }}
                disabled={!transferModal.reason.trim()}
              >
                Proceed to Transfer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Reason Dialog (for authorized bookings) */}
      {editReasonDialog.isOpen && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: "500px" }}>
            <div className="modal-header">
              <h3>Edit Reason Required</h3>
              <button
                className="close-button"
                onClick={() =>
                  setEditReasonDialog({
                    isOpen: false,
                    editReason: "",
                    editDetails: "",
                    error: "",
                  })
                }
              >
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: "16px", color: "#6b7280" }}>
                This booking has been authorized. Please provide a reason for
                this edit:
              </p>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: 500 }}>
                  Reason for Edit <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  value={editReasonDialog.editReason}
                  onChange={(e) =>
                    setEditReasonDialog((prev) => ({
                      ...prev,
                      editReason: e.target.value,
                      error: "",
                    }))
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                  }}
                >
                  <option value="">Select reason...</option>
                  <option value="Customer Details Change">
                    Customer Details Change
                  </option>
                  <option value="Payment Terms Update">
                    Payment Terms Update
                  </option>
                  <option value="Payment Amount Change">
                    Payment Amount Change
                  </option>
                  <option value="Unit Charges Update">
                    Unit Charges Update
                  </option>
                  <option value="Contact Information Update">
                    Contact Information Update
                  </option>
                  <option value="Applicant Name Correction">
                    Applicant Name Correction
                  </option>
                  <option value="Other">Other (please specify)</option>
                </select>
              </div>

              {editReasonDialog.editReason === "Other" && (
                <div style={{ marginBottom: "16px" }}>
                  <label style={{ display: "block", marginBottom: "8px", fontWeight: 500 }}>
                    Please specify <span style={{ color: "red" }}>*</span>
                  </label>
                  <textarea
                    value={editReasonDialog.editDetails}
                    onChange={(e) =>
                      setEditReasonDialog((prev) => ({
                        ...prev,
                        editDetails: e.target.value,
                        error: "",
                      }))
                    }
                    placeholder="Enter detailed reason..."
                    rows={3}
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                      resize: "vertical",
                    }}
                  />
                </div>
              )}

              {editReasonDialog.error && (
                <div style={{ color: "red", marginBottom: "16px" }}>
                  {editReasonDialog.error}
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  className="btn btn-secondary"
                  onClick={() =>
                    setEditReasonDialog({
                      isOpen: false,
                      editReason: "",
                      editDetails: "",
                      error: "",
                    })
                  }
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    if (!editReasonDialog.editReason) {
                      setEditReasonDialog((prev) => ({
                        ...prev,
                        error: "Please select a reason",
                      }));
                      return;
                    }
                    if (
                      editReasonDialog.editReason === "Other" &&
                      !editReasonDialog.editDetails.trim()
                    ) {
                      setEditReasonDialog((prev) => ({
                        ...prev,
                        error: "Please provide details",
                      }));
                      return;
                    }
                    performUpdate();
                  }}
                  disabled={!editReasonDialog.editReason}
                >
                  Confirm & Update
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditBooking;
